
import { Options, Vue } from "vue-class-component";
import { isDefined, scrollToTop } from "@/utils";
import LazyLoadContainer from "@/components/LazyLoadComponent.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import { Services } from "@/Services";
import { SpeechContainer } from "@/entities/SpeechContainer";
import { SpeechFilter } from "@/entities/SpeechFilter";
import { Observable, of, zipAll } from "rxjs";
import SpeakerDetailDialog from "@/components/SpeakerDetailDialog.vue";
import { Speech } from "./entities/Speech";
import moment from "moment";
import { Watch } from "vue-property-decorator";
import Stand from "@/entities/Stand";
import StandDialog from "@/components/StandDialog.vue";
import { ExhibitorContainer } from "@/entities/ExhibitorContainer";
import ExhibitorDetailDialog from "@/components/ExhibitorDetailDialog.vue";
import { Role } from "@/entities/Role";
import { Speaker } from "@/entities/Speaker";
import langHelper from "@/locales/langHelper";
import QrCodeDialog from "@/components/QrCodeDialog.vue";
import { QrcodeCanvas } from "qrcode.vue";

const ics = require("ics");

@Options({
  components: {
    QrCodeDialog,
    QrcodeCanvas,
    ExhibitorDetailDialog, SpeakerDetailDialog, LazyLoadContainer, LoadingSpinner, StandDialog
  }
})
export default class App extends Vue {

  private acomSpecialIds = ['S10'];

  searchValue: string = "";
  isLoading: boolean = true;
  speechContainer: SpeechContainer = null;
  speechFilter: SpeechFilter = new SpeechFilter({});
  speakers: Speaker[] = [];
  activeFilters: Record<string, string> = {};
  isFilterActive: boolean = false;
  exhibitorContainer: ExhibitorContainer = null;
  roles: Role[] = [];
  activeLetterFilter: string = 'all';

  enableFavorites: boolean = false;


  constructor(props: any) {
    super(props);

    let themes: string[] = null;
    const appRoot = document.getElementById("egcf-program-list");
    if (isDefined(appRoot)) {
      const themesAttribute = appRoot.attributes.getNamedItem("themes");
      if (themesAttribute !== null) {
        themes = themesAttribute.value.split("|");
      }
    }
    this.enableFavorites = process.env.VUE_APP_ENABLE_FAVORITES === 'true';
    const observables: Observable<any>[] = [
      Services.getSpeechFilters(themes),
      Services.getSpeeches(themes),
      Services.getExhibitors(),
      Services.getSpeakers(),
      Services.getRoles()
    ];

    of(...observables).pipe(zipAll()).subscribe(values => {
      this.speechFilter = values[0];
      this.speechContainer = values[1];
      this.exhibitorContainer = values[2];
      this.speakers = values[3];
      this.roles = values[4];
      this.isLoading = false;
    });
  }

  mounted() {
    window.addEventListener("scroll", this.handleScrollEvent);
  }

  handleScrollEvent(): void {
    if (isDefined(this.$refs.scrollToTopButton)) {
      if (document.body.scrollTop > 200 || document.documentElement.scrollTop > 200) {
        (this.$refs.scrollToTopButton as HTMLElement).style.display = "block";
      } else {
        (this.$refs.scrollToTopButton as HTMLElement).style.display = "none";
      }
    }
  }

  @Watch("isLoading")
  onLoadingChanged() {
    if (!this.isLoading) {
      this.$nextTick(() => {
        this.addClickListenerToSpeakerNamesFromInfo();
        this.scrollToHash();
      });
    }
  }


  private openQrCodeDialog(event: Event): void {
    event.stopImmediatePropagation();
    const qrCodeDialog = this.$refs.qrCodeDialog as QrCodeDialog;
    if (isDefined(qrCodeDialog)) {
      let favoritesString = window.localStorage.getItem("egcf_favorite_program_list");
      if (!isDefined(favoritesString)) {
        return;
      }
      const qrCodeContent = {
        prj: process.env.VUE_APP_PROJECT,
        evt: JSON.parse(favoritesString)
      };
      qrCodeDialog.openDialog(JSON.stringify(qrCodeContent));
    }
  }

  private toggleFavorite(speech: Speech, event: Event): void {
    if (event) {
      event.stopImmediatePropagation();
    }
    let favoritesString = window.localStorage.getItem("egcf_favorite_program_list");
    let favoritesList: String[];
    if (this.isFavorite(speech)) {
      favoritesList = JSON.parse(favoritesString);
      const index = favoritesList.indexOf(speech.id);
      if (index !== -1) {
        favoritesList.splice(index, 1);
      }
    } else {
      if (!isDefined(favoritesString)) {
        favoritesList = [speech.id];
      } else {
        favoritesList = JSON.parse(favoritesString);
        favoritesList.push(speech.id);
      }
    }
    window.localStorage.setItem("egcf_favorite_program_list", JSON.stringify(favoritesList));
    speech.reload();
    if (this.activeLetterFilter === 'favorites') {
      this.onFilterChanged()
    }
  }

  private hasFavorites(): boolean {
    let favoritesString = window.localStorage.getItem("egcf_favorite_program_list");
    let favoritesList;
    if (!isDefined(favoritesString)) {
      return false;
    } else {
      favoritesList = JSON.parse(favoritesString);
    }
    return favoritesList.length > 0;
  }

  private isFavorite(speech: Speech): boolean {
    let favoritesString = window.localStorage.getItem("egcf_favorite_program_list");
    let favoritesList;
    if (!isDefined(favoritesString)) {
      return false;
    } else {
      favoritesList = JSON.parse(favoritesString);
    }
    return favoritesList.indexOf(speech.id) !== -1;
  }

  private scrollToHash(): void {
    if (window.location.hash) {
      let target = window.location.hash;
      if (target.startsWith("#")) target = target.substring(1);
      let div = document.getElementById(target);
      if (div) {
        div.scrollIntoView(true);
      }
    }
  }

  private addClickListenerToSpeakerNamesFromInfo(): void {
    const allElements = document.getElementsByTagName("div");

    for (let i = 0, n = allElements.length; i < n; i++) {
      const attribute = allElements[i].getAttribute("data-speaker-id");
      if (isDefined(attribute)) {
        if (allElements[i].hasChildNodes()) {
          const speakerName = allElements[i].getElementsByTagName("b");
          speakerName[0].style.cursor = "pointer";
        }
        const speaker = this.getSpeakerById(attribute);
        if (isDefined(speaker)) {
          allElements[i].onclick = (event) => {
            this.openSpeakerDetailDialog(speaker, event);
          };
        }
      }
    }
  }

  scrollTop(): void {
    scrollToTop();
  }

  onSearchValueInput(event): void {
    if (isDefined(this.speechContainer)) {
      this.speechContainer.filterSpeeches(this.searchValue, this.activeFilters,this.activeLetterFilter);
    }
  }

  toggleFilter(): void {
    this.isFilterActive = !this.isFilterActive;
    if (!this.isFilterActive) {
      this.resetFilter();
    }
  }

  resetFilter(): void {
    this.activeFilters = {};
    this.onFilterChanged();
  }

  onFilterChanged(event?): void {
    for (const [key, value] of Object.entries(this.activeFilters)) {
      if (value.length === 0) {
        delete this.activeFilters[key];
      }
    }
    if (isDefined(this.speechContainer)) {
      this.speechContainer.filterSpeeches(this.searchValue, this.activeFilters,this.activeLetterFilter);
    }
  }



  hasActiveFilter(): boolean {
    return Object.entries(this.activeFilters).length > 0;
  }

  getLangForSpeechById(id): string {
    if (isDefined(id) && isDefined(this.speechFilter)) {
      for (const [key, value] of Object.entries(this.speechFilter.filters)) {
        for (const filter of value) {
          if (filter.id === id) {
            return filter.title;
          }
        }
      }
    }
    return undefined;
  }

  openStandDialog(stand: Stand, event: Event): void {
    event.stopImmediatePropagation();
    console.log(stand);
    if (isDefined(this.$refs.standDialog)) {
      (this.$refs.standDialog as StandDialog).openDialog(stand);
    }
  }

  openExhibitorDialog(exhibitorId: string, event: Event): void {
    event.stopImmediatePropagation();
    if (isDefined(this.$refs.exhibitorDetailDialog)) {
      const exhibitor = this.exhibitorContainer.exhibitors.find(e => e.id === exhibitorId);
      (this.$refs.exhibitorDetailDialog as ExhibitorDetailDialog).openDialog(exhibitor);
    }
  }

  getSpeakerById(id: string): Speaker {
    if (isDefined(this.speakers) && isDefined(id)) {
      const index = this.speakers.findIndex(s => s.id === id);
      if (index > -1) {
        return this.speakers[index];
      }
    }
    return undefined;
  }


  openSpeakerDetailDialog(speaker: Speaker, event: Event): void {
    if (isDefined(event)) {
      event.stopImmediatePropagation();
    }

    if (isDefined(this.$refs.speakerDetailDialog)) {
      let speechesForSpeaker: Speech[] = this.speechContainer.getSpeechesForSpeakerById(speaker.id);
      (this.$refs.speakerDetailDialog as SpeakerDetailDialog).openDialog(speaker, speechesForSpeaker);
    }
  }

  downloadIcal(speech: Speech, event: Event): void {
    event.stopImmediatePropagation();
    const start = moment(speech.begin);
    const end = moment(speech.end);

    let title = speech.title;
    const prefix = process.env.VUE_APP_PROJECT_NAME_FOR_ICAL;
    if (isDefined(prefix) && prefix.length > 0) {
      title = `${prefix} ${speech.title}`;
    }

    const startParams = [start.year(), start.month() + 1, start.date()];
    const endParams = [];

    if (!speech.isAllDay) {
      startParams.push(start.hour(), start.minute());
      endParams.push(end.year(), end.month() + 1, end.date(), end.hour(), end.minute());
    } else {
      const tomorrow = end.add(1, "days");
      endParams.push(end.year(), tomorrow.month() + 1, tomorrow.date());
    }

    ics.createEvent({
      start: startParams,
      end: endParams,
      title: title,
      location: speech.place
    }, ((error, value) => {
      if (error) {
        console.log(error);
        return;
      }
      const blob = new Blob([value], { type: "text/calendar" });
      const icsFile = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = icsFile;
      a.target = "_blank";
      a.download = speech.title;
      a.type = "text/calendar";
      a.click();
    }));
  }

  getColorCSS(speech): string {
    return isDefined(speech.color) && speech.color.length === 6 ? ("background-color: #" + speech.color + ";color:" + speech.categoryColor) : "";
  }


  getBorderColorCSS(speech): string {
    return isDefined(speech.color) && speech.color.length === 6 ? ("border-left: 4px solid #" + speech.color + ";") : "";
  }

  getIconColorCSS(speech): string {
    return speech.getIconStyle;
  }

  getIdForDate(date: string): string {
    const lang = langHelper.getCurrentLang();
    if (lang == "de") {
      return moment(date, "Do MMMM YYYY").format("YYYY-MM-DD");
    } else {
      return moment(date, "D MMMM YYYY").format("YYYY-MM-DD");
    }
  }

  private onLetterClicked(letter: string): void {
    this.activeLetterFilter = letter;
    this.onFilterChanged();
  }
}
